import React, { useCallback } from 'react';
import cn from 'classnames';

import Tooltip from 'components/generic/Tooltip.component';
import useTip from 'hooks/useTip.hook';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';
import isTruthy from 'utils/predicates/isTruthy.util';

import CN_CHECK from './Checkbox.module.scss';
import CN_RADIO from './Radio.module.scss';
import CN_S_RADIO from './SimpleRadio.module.scss';

// TODO: remove one of these types, most likely Checkbox
// NOTE: page salon/12259/hr/timeoff used both variants - same styles

const Checkbox = ({ name, tag, checked, onCheck, children, disabled }) => {
  const { isDarkMode } = useTheme();

  const { val: isSelected, tip: toggleSelect } = useTip(checked);

  const checkboxClasses = cn({
    [CN_CHECK.checkbox]: true,
    [CN_CHECK.selected]: isSelected,
    [CN_CHECK.notSelected]: !isSelected,
  });

  const handleClick = useCallback(() => {
    toggleSelect();
    call('Checkbox.onCheck', onCheck, { name, value: !isSelected });
  }, [toggleSelect, onCheck, name, isSelected]);

  return (
    <div
      data-bem="Checkbox--typed-option"
      data-tag={tag}
      data-name={name}
      className={cn({
        [CN_CHECK.component]: true,
        [CN_CHECK.dark]: isDarkMode,
        [CN_CHECK.disabled]: disabled,
      })}
    >
      <div role="button" tabIndex="0" className={checkboxClasses} onClick={handleClick}>
        <div className={CN_CHECK.checkIcon} />
      </div>
      {isTruthy(children) && <div className={CN_CHECK.content}>{children}</div>}
    </div>
  );
};

const Radio = ({ name, tag, label, value, onChange, variant }) => {
  const { isDarkMode } = useTheme();

  return (
    <label
      data-bem="Radio--typed-option"
      data-tag={tag}
      data-name={name}
      className={cn({
        [CN_RADIO.component]: true,
        [CN_RADIO[`${variant}Variant`]]: variant,
        [CN_RADIO.dark]: isDarkMode,
      })}
    >
      <input className={CN_RADIO.radioInput} type="radio" onChange={onChange} value={value} checked={value} />
      <span className={CN_RADIO.checkMark} />
      {isTruthy(label) && <div className={CN_RADIO.text}>{label}</div>}
    </label>
  );
};

const SimpleCheckbox = ({ name, tag, checked, onCheck, children, disabled }) => {
  const { isDarkMode } = useTheme();

  const handleClick = useCallback(() => void call('SimpleCheckbox.onCheck', onCheck, { name, value: !checked }), [onCheck, name, checked]);

  return (
    <div
      data-bem="SimpleCheckbox--typed-option"
      role="button"
      onClick={handleClick}
      tabIndex="0"
      data-tag={tag}
      data-name={name}
      className={cn({
        [CN_CHECK.component]: true,
        [CN_CHECK.dark]: isDarkMode,
        [CN_CHECK.disabled]: disabled,
      })}
    >
      <div
        className={cn({
          [CN_CHECK.checkbox]: true,
          [CN_CHECK.selected]: checked,
          [CN_CHECK.notSelected]: !checked,
        })}
      >
        <div className={CN_CHECK.checkIcon} />
      </div>
      {isTruthy(children) && <div className={CN_CHECK.content}>{children}</div>}
    </div>
  );
};

const SimpleRadio = ({ name, tag, children, checked, disabled, onClick }) => {
  const { isDarkMode } = useTheme();

  const isChecked = fromAnyToBool(checked);

  return (
    <div
      data-bem="SimpleRadio--typed-option"
      data-tag={tag}
      data-name={name}
      className={cn({
        [CN_S_RADIO.component]: true,
        [CN_S_RADIO.isSelected]: isChecked,
        [CN_S_RADIO.isDisabled]: disabled,
        [CN_S_RADIO.dark]: isDarkMode,
      })}
      onClick={onClick}
    >
      <div className={CN_S_RADIO.iconContainer}>{isChecked && <div className={CN_S_RADIO.icon} />}</div>
      <div className={CN_S_RADIO.name}>{children}</div>
    </div>
  );
};

const TooltipRadio = ({ onCheck, tooltip, ...props }) => {
  const { disabled, checked } = props ?? {};
  const name = props.name;

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (isNotFn(onCheck)) return;

    onCheck({ name, value: !checked });
  }, [name, onCheck, checked, disabled]);

  return disabled ? (
    <Tooltip variant="tertiary" content={tooltip}>
      <SimpleRadio onClick={handleClick} {...props} />
    </Tooltip>
  ) : (
    <SimpleRadio onClick={handleClick} {...props} />
  );
};

const TooltipCheckbox = ({ tooltip, ...props }) =>
  props.disabled ? (
    <Tooltip variant="tertiary" content={tooltip}>
      <SimpleRadio {...props} />
    </Tooltip>
  ) : (
    <SimpleRadio {...props} />
  );

const TypedOption = ({ type, name: nameFromProps, tag: tagFromProps, ...props }) => {
  // Just some rework on some props here so that we don't repeat the same code inside the different child components
  const extra = {
    name: fromAnyToTrimmed(nameFromProps),
    tag: fromAnyToTrimmed(tagFromProps),
  };

  if (type === 'radio') return <Radio {...props} {...extra} />;
  if (type === 'checkbox') return <Checkbox {...props} {...extra} />;

  if (type === 'simple-checkbox') return <SimpleCheckbox {...props} {...extra} />;

  if (type === 'tooltip-checkbox') return <TooltipCheckbox {...props} {...extra} />;
  if (type === 'tooltip-radio') return <TooltipRadio {...props} {...extra} />;

  return <Radio {...props} {...extra} />;
};

export default TypedOption;
